import { gql, useMutation, useQuery } from '@apollo/client';
import { useCallback } from 'react';
import {
  CreateSubjectForDuplicationMutation,
  CreateSubjectForDuplicationMutationVariables,
  GetAssignmentsForDuplicationQuery,
  GetAssignmentsForDuplicationQueryVariables,
  GetPrintHeadingsForDuplicationQuery,
  GetPrintHeadingsForDuplicationQueryVariables,
  GetPrintIssuesForDuplicationQuery,
  GetPrintIssuesForDuplicationQueryVariables,
  GetPrintPublicationsForDuplicationQuery,
  GetPrintPublicationsForDuplicationQueryVariables,
  GetUnitsForDuplicationQuery,
  GetUnitsForDuplicationQueryVariables,
  UpdateAssignmentForDuplicationMutation,
  UpdateAssignmentForDuplicationMutationVariables,
} from '../../../__generated__/queries-topic';
import { useGetter } from '../../../apollo/useGetter';

export const GET_ASSIGNMENTS_FOR_DUPLICATION = gql`
  query GetAssignmentsForDuplication(
    $where: AssignmentFilterInput
    $orderBy: [AssignmentOrderingInput!]
    $skip: UnsignedInt
    $first: UnsignedInt!
  ) {
    assignments(where: $where, orderBy: $orderBy, skip: $skip, first: $first) {
      id
      subject {
        id
        title
      }
      rawArticle {
        id
        lastVersion {
          id
        }
      }
      printHeading {
        title
        printIssue {
          title
          printPublication {
            title
          }
        }
      }
    }
  }
`;

export const useAssignmentsGetter = () =>
  useGetter<
    GetAssignmentsForDuplicationQuery,
    GetAssignmentsForDuplicationQueryVariables
  >(GET_ASSIGNMENTS_FOR_DUPLICATION);

export const GET_PRINT_PUBLICATIONS_FOR_DUPLICATION = gql`
  query GetPrintPublicationsForDuplication(
    $where: PrintPublicationFilterInput
    $orderBy: [PrintPublicationOrderingInput!]
    $skip: UnsignedInt
    $first: UnsignedInt!
  ) {
    printPublications(
      where: $where
      orderBy: $orderBy
      skip: $skip
      first: $first
    ) {
      id
      title
    }
  }
`;

export const useGetPrintPublications = (
  variables: GetPrintPublicationsForDuplicationQueryVariables,
) =>
  useQuery<
    GetPrintPublicationsForDuplicationQuery,
    GetPrintPublicationsForDuplicationQueryVariables
  >(GET_PRINT_PUBLICATIONS_FOR_DUPLICATION, { variables });

export const GET_PRINT_ISSUES_FOR_DUPLICATION = gql`
  query GetPrintIssuesForDuplication(
    $where: PrintIssueFilterInput
    $orderBy: [PrintIssueOrderingInput!]
    $skip: UnsignedInt
    $first: UnsignedInt!
  ) {
    printIssues(where: $where, orderBy: $orderBy, skip: $skip, first: $first) {
      id
      title
    }
  }
`;

export const useGetPrintIssues = (
  variables: GetPrintIssuesForDuplicationQueryVariables,
) =>
  useQuery<
    GetPrintIssuesForDuplicationQuery,
    GetPrintIssuesForDuplicationQueryVariables
  >(GET_PRINT_ISSUES_FOR_DUPLICATION, { variables });

export const GET_PRINT_HEADINGS_FOR_DUPLICATION = gql`
  query GetPrintHeadingsForDuplication(
    $where: PrintHeadingFilterInput
    $orderBy: [PrintHeadingOrderingInput!]
    $skip: UnsignedInt
    $first: UnsignedInt!
  ) {
    printHeadings(
      where: $where
      orderBy: $orderBy
      skip: $skip
      first: $first
    ) {
      id
      title
    }
  }
`;

export const useGetPrintHeadings = (
  variables: GetPrintHeadingsForDuplicationQueryVariables,
) =>
  useQuery<
    GetPrintHeadingsForDuplicationQuery,
    GetPrintHeadingsForDuplicationQueryVariables
  >(GET_PRINT_HEADINGS_FOR_DUPLICATION, { variables });

export const GET_UNITS_FOR_DUPLICATION = gql`
  query GetUnitsForDuplication(
    $where: UnitFilterInput
    $orderBy: [UnitOrderingInput!]
    $skip: UnsignedInt
    $first: UnsignedInt!
  ) {
    units(where: $where, orderBy: $orderBy, skip: $skip, first: $first) {
      id
    }
  }
`;

export const useUnitsGetter = () =>
  useGetter<GetUnitsForDuplicationQuery, GetUnitsForDuplicationQueryVariables>(
    GET_UNITS_FOR_DUPLICATION,
  );

export const CREATE_SUBJECT_FOR_DUPLICATION = gql`
  mutation CreateSubjectForDuplication($data: SubjectCreationInput!) {
    createSubject(data: $data) {
      assignments(first: 1) {
        id
      }
    }
  }
`;

export const useCreateSubject = () => {
  const [createSubject] = useMutation<
    CreateSubjectForDuplicationMutation,
    CreateSubjectForDuplicationMutationVariables
  >(CREATE_SUBJECT_FOR_DUPLICATION, {
    refetchQueries: [GET_ASSIGNMENTS_FOR_DUPLICATION],
  });

  return useCallback(
    (variables: CreateSubjectForDuplicationMutationVariables) =>
      createSubject({ variables }),
    [createSubject],
  );
};

export const UPDATE_ASSIGNMENT_FOR_DUPLICATION = gql`
  mutation UpdateAssignmentForDuplication(
    $data: AssignmentUpdateInput!
    $where: AssignmentUniqueFilterInput!
  ) {
    updateAssignment(data: $data, where: $where) {
      id
    }
  }
`;

export const useUpdateAssignment = () => {
  const [updateAssignment] = useMutation<
    UpdateAssignmentForDuplicationMutation,
    UpdateAssignmentForDuplicationMutationVariables
  >(UPDATE_ASSIGNMENT_FOR_DUPLICATION);

  return useCallback(
    (variables: UpdateAssignmentForDuplicationMutationVariables) =>
      updateAssignment({ variables }),
    [updateAssignment],
  );
};
